import { type KeyboardDownCapture } from '@canvas/input-types/keyboard-events-types';

import { type SelectionsMachine } from '../../../machine/selection-machine';
import { hasActiveEdit } from '../../../machine/utils/has-active-edit';

const NON_PRINTABLE_KEYS = new Set(['↑', '↓', '←', '→']);

/**
 * Maps legacy focused-cell key actions to selection machine events.
 *
 * The handler preserves the previous table keyboard behavior: enter starts
 * editing, delete/backspace clears cells, escape deselects, and printable
 * characters start editing with an initial key. Navigation keys are intentionally
 * ignored here so movement can be owned by a separate keyboard input.
 */
export function handleTableCellActionsKeyDown(
  capture: KeyboardDownCapture,
  machine: SelectionsMachine
): void {
  const state = machine.getState();

  if (!state.focusedCell || hasActiveEdit(state)) {
    return;
  }

  const { key, event: domEvent } = capture.properties;
  const hasModifier = domEvent.ctrlKey || domEvent.metaKey;

  switch (key) {
    case 'Enter': {
      if (state.mode === 'cell') {
        domEvent.preventDefault();
        machine.dispatch({
          type: 'startEditing',
          cell: state.focusedCell,
          source: 'keyboard'
        });
        capture.properties.consumed = true;
      }
      break;
    }

    case 'Backspace':
    case 'Delete': {
      if (!hasModifier) {
        domEvent.preventDefault();
        machine.dispatch({
          type: 'clearCells',
          source: 'keyboard'
        });
        capture.properties.consumed = true;
      }
      break;
    }

    case 'Escape': {
      domEvent.preventDefault();
      machine.dispatch({ type: 'deselectAll', source: 'keyboard' });
      capture.properties.consumed = true;
      break;
    }

    default: {
      if (
        state.mode === 'cell' &&
        key.length === 1 &&
        !hasModifier &&
        !NON_PRINTABLE_KEYS.has(key)
      ) {
        machine.dispatch({
          type: 'startEditing',
          cell: state.focusedCell,
          source: 'keyboard',
          initialKey: key
        });
        capture.properties.consumed = true;
      }
      break;
    }
  }
}
